import React, { useMemo } from "react"
import { Common } from "lib/types"
import { CheckLeaf, LeafCheck } from "lib/types/workflow"
import { CheckType } from "lib/types/workflowConditions"

import SingleSelectSimple from "components/Ui/Form/SingleSelectSimple"
import { FormLabel, FormSection } from "./NodeEditorSidebar.styled"
import {
  CHECK_DESCRIPTORS,
  CHECK_TYPE_GROUPS,
  CheckDescriptor
} from "./conditionSchema"
import {
  makeDefaultComparisonInteger,
  makeDefaultComparisonString,
  makeDefaultLeaf
} from "./checkFieldHelpers"
import {
  ArrayCountSubForm,
  CustomHttpSubForm,
  FieldBagSubForm,
  OrderDateSubForm
} from "./leafSubForms"

type Props = {
  value: CheckLeaf
  onChange: (next: CheckLeaf) => void
}

/** Build a fresh LeafCheck for the given check type with no fields populated.
 *  The user must add at least one field before the leaf is valid. */
const makeBlankCheck = (type: CheckType): LeafCheck => {
  switch (type) {
    case "AND":
    case "OR":
      // Compounds aren't represented as leaves — fall through to the
      // canonical default that the type select hides anyway.
      return makeDefaultLeaf().check
    case "ORDER_LINES":
    case "SHIPPING_FEES":
    case "GIFTS":
      return { type, count: { length: makeDefaultComparisonInteger() } }
    case "ORDER_DATE":
      return { type, ...makeDefaultComparisonString() }
    case "CUSTOM_HTTP":
      return { type, endpoint: "" }
    default:
      return { type, fields: {} } as LeafCheck
  }
}

const LeafCheckEditor = ({ value, onChange }: Props) => {
  const descriptor: CheckDescriptor = CHECK_DESCRIPTORS[value.check.type]
  const handleCheckTypeChange = (nextType: CheckType) => {
    onChange({ ...value, check: makeBlankCheck(nextType) })
  }

  /* react-select accepts grouped options shaped as { label, options[] }.
     SingleSelectSimple's typed Common.Option[] prop doesn't model groups,
     so we cast through unknown — the underlying react-select handles
     groups natively at runtime. */
  const checkTypeOptions = useMemo(
    () =>
      CHECK_TYPE_GROUPS.map((g) => ({
        label: g.label,
        options: g.options.map((o) => ({ value: o.value, label: o.label }))
      })) as unknown as Common.Option[],
    []
  )
  const selectedCheckType: Common.Option = useMemo(
    () => ({
      value: value.check.type,
      label: descriptor.label
    }),
    [descriptor.label, value.check.type]
  )

  return (
    <>
      <FormSection>
        <FormLabel>Check Type</FormLabel>
        <SingleSelectSimple
          options={checkTypeOptions}
          value={selectedCheckType}
          setValue={(v: string) => handleCheckTypeChange(v as CheckType)}
          isSearchable
        />
      </FormSection>

      {descriptor.shape === "endpoint" && (
        <CustomHttpSubForm leaf={value} onChange={onChange} />
      )}

      {descriptor.shape === "single-datetime" && (
        <OrderDateSubForm leaf={value} onChange={onChange} />
      )}

      {descriptor.shape === "array-count" && (
        <ArrayCountSubForm leaf={value} onChange={onChange} />
      )}

      {(descriptor.shape === "field-bag" || descriptor.shape === "address-bag") && (
        <FieldBagSubForm leaf={value} onChange={onChange} descriptor={descriptor} />
      )}
    </>
  )
}

export default LeafCheckEditor
